import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../Localization.jsx";
import {
  Modal,
  Box,
  Flex,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Alert,
  Checkbox,
  Loader,
  Paper,
  Anchor,
  ScrollArea,
  CloseButton,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBooks,
  IconCheck,
  IconInfoCircle,
  IconLock,
  IconX,
} from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../theme/brandTheme.js";

/**
 * ExtraLocaleDialog — the in-app version of the 預製教材範本選單 survey.
 *
 * Reads ONE overview from the server (eligibility + quota + the school's
 * permanent selections + the enabled catalog) and submits picks. Two rules
 * shape everything here:
 *
 *   1. The allowance is the SUBSCRIPTION's (PLAN_EXTRA_LOCALE), never a
 *      constant — the dialog only renders what the server computed.
 *   2. Selections are PERMANENT (範本一經選定，不設更換). There is no
 *      unselect for already-recorded rows, and submitting goes through an
 *      explicit confirm step that repeats the warning.
 *
 * The quota is shared by the whole school: any teacher spends it, so the
 * review list shows who picked each template and when.
 */

// Centre contacts printed under the notes. Numbered keys (not names) so the
// roster can change in the localization files alone.
const CONTACT_KEYS = [
  'EXTRA_LOCALE_CONTACT_1',
  'EXTRA_LOCALE_CONTACT_3',
];

class ExtraLocaleDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      loadFailed: false,
      overview: null,
      availableSelected: [], // available rows ticked before the Add action
      picked: [],        // rows added to the right column but not yet submitted
      confirming: false, // true = the confirm step is shown
      submitting: false,
      submitError: "",
    };
    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.fetchOverview();
    }
  }

  fetchOverview() {
    const root = this.props.root;
    if (!root || !root.refs || !root.refs.API) {
      return;
    }
    this.setState({
      loading: true,
      loadFailed: false,
      overview: null,
      availableSelected: [],
      picked: [],
      confirming: false,
      submitError: "",
    });
    root.refs.API.request(
      "get_extra_locale_overview",
      { school_id: root.getMySchoolId() },
      this.fetchOverviewOK,
      this.fetchOverviewFailed
    );
  }

  fetchOverviewOK(res) {
    const overview = res && res.body && res.body.result && typeof res.body.result === 'object'
      ? res.body.result
      : null;
    this.setState({ loading: false, loadFailed: !overview, overview: overview });
  }

  fetchOverviewFailed(err) {
    console.log("get_extra_locale_overview failed", err);
    this.setState({ loading: false, loadFailed: true, overview: null });
  }

  remaining() {
    const overview = this.state.overview;
    if (!overview) {
      return 0;
    }
    return Math.max(0, (Number(overview.remaining) || 0) - this.state.picked.length);
  }

  toggleAvailable(id) {
    const availableSelected = this.state.availableSelected.slice();
    const index = availableSelected.indexOf(id);
    if (index >= 0) {
      availableSelected.splice(index, 1);
    } else {
      if (availableSelected.length >= this.remaining()) {
        return;
      }
      availableSelected.push(id);
    }
    this.setState({ availableSelected: availableSelected, submitError: "" });
  }

  handleAddSelected() {
    if (this.state.availableSelected.length === 0) {
      return;
    }
    this.setState({
      picked: this.state.picked.concat(this.state.availableSelected),
      availableSelected: [],
      submitError: "",
    });
  }

  removePending(id) {
    this.setState({
      picked: this.state.picked.filter((pickedId) => pickedId !== id),
      submitError: "",
    });
  }

  handleSubmitClicked() {
    if (this.state.picked.length === 0) {
      return;
    }
    this.setState({ confirming: true, submitError: "" });
  }

  handleConfirmBack() {
    this.setState({ confirming: false });
  }

  handleConfirmSubmit() {
    const root = this.props.root;
    if (!root || !root.refs || !root.refs.API || this.state.submitting) {
      return;
    }
    this.setState({ submitting: true, submitError: "" });
    root.refs.API.request(
      "select_extra_locales",
      {
        school_id: root.getMySchoolId(),
        extra_locale_ids: this.state.picked,
      },
      this.submitOK,
      this.submitFailed
    );
  }

  submitOK(res) {
    this.setState({
      submitting: false,
      confirming: false,
      availableSelected: [],
      picked: [],
    });
    const root = this.props.root;
    if (root && root.addNotification) {
      root.addNotification(Localization.strings.EXTRA_LOCALE_SUBMIT_SUCCESS, true);
    }
    this.fetchOverview();
  }

  submitFailed(err) {
    console.log("select_extra_locales failed", err);
    const detail = err && err.body && err.body.error_detail ? err.body.error_detail : null;
    let message = Localization.strings.EXTRA_LOCALE_SUBMIT_FAILED;
    if (detail && detail.error === 'quota_exceeded') {
      message = (Localization.strings.EXTRA_LOCALE_QUOTA_EXCEEDED || '')
        .replace('{0}', detail.used != null ? detail.used : '?')
        .replace('{1}', detail.quota != null ? detail.quota : '?');
    } else if (detail && detail.error === 'nothing_to_add') {
      message = Localization.strings.EXTRA_LOCALE_NOTHING_TO_ADD;
    } else if (err && err.body && err.body.remarks) {
      message = Localization.strings.EXTRA_LOCALE_SUBMIT_FAILED + ": " + err.body.remarks;
    }
    // The server refused with fresher state than ours (another teacher may
    // have submitted first) — re-sync instead of leaving stale checkboxes.
    this.setState({ submitting: false, confirming: false, submitError: message });
    this.fetchOverview();
    // fetchOverview clears submitError; restore it after the reset so the
    // teacher sees why the submit bounced alongside the fresh state.
    this.setState({ submitError: message });
  }

  /** Catalog rows grouped by category, in server (survey) order. */
  groupedCatalog() {
    const overview = this.state.overview;
    const catalog = overview && Array.isArray(overview.catalog) ? overview.catalog : [];
    const groups = [];
    const byCategory = {};
    catalog.forEach((row) => {
      const category = row.category || "";
      if (!Object.prototype.hasOwnProperty.call(byCategory, category)) {
        byCategory[category] = [];
        groups.push({ category: category, rows: byCategory[category] });
      }
      byCategory[category].push(row);
    });
    return groups;
  }

  selectedIdSet() {
    const overview = this.state.overview;
    const selections = overview && Array.isArray(overview.selections) ? overview.selections : [];
    const set = {};
    selections.forEach((row) => { set[Number(row.extra_locale_id)] = true; });
    return set;
  }

  itemLabel(row) {
    return row.code ? (row.code + "：" + row.name) : row.name;
  }

  catalogRows() {
    const overview = this.state.overview;
    return overview && Array.isArray(overview.catalog) ? overview.catalog : [];
  }

  pendingRows() {
    return this.catalogRows()
      .filter((row) => this.state.picked.indexOf(Number(row.id)) >= 0);
  }

  renderAddedList() {
    const overview = this.state.overview;
    const selections = overview && Array.isArray(overview.selections) ? overview.selections : [];
    const pendingRows = this.pendingRows();
    if (selections.length === 0 && pendingRows.length === 0) {
      return (
        <Stack align="center" justify="center" gap={6} style={{ minHeight: 160 }}>
          <IconBooks size={30} color="var(--mantine-color-gray-5)" stroke={1.5} />
          <Text c="dimmed" size="sm" ta="center">
            {Localization.strings.EXTRA_LOCALE_NONE_SELECTED}
          </Text>
        </Stack>
      );
    }
    return (
      <Stack gap="xs">
        {pendingRows.map((row) => (
          <Paper key={`pending-${row.id}`} withBorder p="sm" radius="md" bg="brand.0">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Box style={{ minWidth: 0 }}>
                <Text size="sm" fw={600}>{this.itemLabel(row)}</Text>
                <Text size="xs" c="dimmed" mt={2}>{row.category || '-'}</Text>
                <Badge size="xs" variant="light" color="brand" mt={6}>
                  {Localization.strings.EXTRA_LOCALE_PENDING_ROW || '準備提交'}
                </Badge>
              </Box>
              <Tooltip label={Localization.strings.EXTRA_LOCALE_REMOVE_PENDING || '移除'}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => this.removePending(Number(row.id))}
                  aria-label={Localization.strings.EXTRA_LOCALE_REMOVE_PENDING || '移除'}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Paper>
        ))}
        {selections.map((row) => (
          <Paper key={`selected-${row.id}`} withBorder p="sm" radius="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Box style={{ minWidth: 0 }}>
                <Text size="sm" fw={600}>{this.itemLabel(row)}</Text>
                <Text size="xs" c="dimmed" mt={2}>{row.category || '-'}</Text>
                <Text size="xs" c="dimmed" mt={5}>
                  {row.user_display_name || row.username || '-'}
                  {' · '}
                  {String(row.time_create || '').slice(0, 10)}
                </Text>
              </Box>
              <Badge
                size="xs"
                color="gray"
                variant="light"
                leftSection={<IconLock size={11} />}
                style={{ flexShrink: 0 }}
              >
                {Localization.strings.EXTRA_LOCALE_ALREADY_SELECTED_ROW}
              </Badge>
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  renderAvailableList() {
    const selected = this.selectedIdSet();
    const remaining = this.remaining();
    const groups = this.groupedCatalog();
    const availableGroups = groups
      .map((group) => ({
        category: group.category,
        rows: group.rows.filter((row) => (
          !selected[Number(row.id)]
          && this.state.picked.indexOf(Number(row.id)) < 0
        )),
      }))
      .filter((group) => group.rows.length > 0);
    if (availableGroups.length === 0) {
      return (
        <Text c="dimmed" size="sm">{Localization.strings.EXTRA_LOCALE_CATALOG_EMPTY}</Text>
      );
    }
    return (
      <Stack gap="md">
        {availableGroups.map((group) => (
          <Box key={group.category || '(none)'}>
            <Text fw={600} size="sm" mb={6}>{group.category || '-'}</Text>
            <Stack gap={6}>
              {group.rows.map((row) => {
                const id = Number(row.id);
                const isChecked = this.state.availableSelected.indexOf(id) >= 0;
                return (
                  <Checkbox
                    key={id}
                    checked={isChecked}
                    disabled={
                      this.state.submitting
                      || (!isChecked && this.state.availableSelected.length >= remaining)
                    }
                    onChange={() => this.toggleAvailable(id)}
                    label={this.itemLabel(row)}
                    styles={{ label: { fontSize: 'var(--mantine-font-size-sm)' } }}
                  />
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  }

  renderConfirmStep() {
    const pickedRows = this.pendingRows();
    return (
      <Modal
        opened={this.state.confirming}
        onClose={this.handleConfirmBack}
        closeOnClickOutside={!this.state.submitting}
        closeOnEscape={!this.state.submitting}
        withCloseButton={false}
        padding={0}
        size="lg"
        radius="md"
        centered
        zIndex={2200}
        styles={{ body: { padding: 0 } }}
      >
        <Group
          justify="space-between"
          px="lg"
          style={{ height: 56, backgroundColor: BRAND_PRIMARY }}
        >
          <Group gap={8}>
            <IconAlertTriangle size={20} color="white" />
            <Text c="white" fw={600} fz="lg">
              {Localization.strings.EXTRA_LOCALE_CONFIRM_TITLE || '確認範本選擇'}
            </Text>
          </Group>
          <CloseButton
            onClick={this.handleConfirmBack}
            disabled={this.state.submitting}
            variant="transparent"
            c="white"
            size="lg"
          />
        </Group>
        <Stack gap="md" p="lg">
          <Alert color="orange" icon={<IconAlertTriangle size={18} />}>
            {Localization.strings.EXTRA_LOCALE_PERMANENT_WARNING}
          </Alert>
          <Text fw={600}>
            {(Localization.strings.EXTRA_LOCALE_CONFIRM_INTRO || '').replace('{0}', String(pickedRows.length))}
          </Text>
          <ScrollArea.Autosize mah={300}>
            <Stack gap="xs">
              {pickedRows.map((row) => (
                <Paper key={row.id} withBorder p="sm" radius="md">
                  <Group gap="sm" wrap="nowrap">
                    <IconCheck size={17} color={BRAND_PRIMARY} style={{ flexShrink: 0 }} />
                    <Box>
                      <Text size="sm" fw={600}>{this.itemLabel(row)}</Text>
                      <Text size="xs" c="dimmed">{row.category || '-'}</Text>
                    </Box>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </ScrollArea.Autosize>
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={this.handleConfirmBack} disabled={this.state.submitting}>
              {Localization.strings.BACK}
            </Button>
            <Button color="red" onClick={this.handleConfirmSubmit} loading={this.state.submitting}>
              {Localization.strings.EXTRA_LOCALE_CONFIRM_SUBMIT}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  renderInformationPanel(headerIntro, resourcesUrl) {
    return (
      <Paper withBorder p="md" radius="md" bg="gray.0">
        <Text size="sm" fw={600} mb="xs">{headerIntro}</Text>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.7fr) minmax(260px, 1fr)',
            gap: 18,
          }}
        >
          <Box>
            <Text size="xs" fw={600}>{Localization.strings.EXTRA_LOCALE_NOTES_TITLE || '備註：'}</Text>
            <ol style={{ margin: '3px 0 0', paddingLeft: 20 }}>
              <li>
                <Text size="xs" component="span" c="orange.9" fw={600}>
                  {Localization.strings.EXTRA_LOCALE_PERMANENT_WARNING}
                </Text>
              </li>
              <li><Text size="xs" component="span">{Localization.strings.EXTRA_LOCALE_NOTE_PARTIAL}</Text></li>
              <li><Text size="xs" component="span">{Localization.strings.EXTRA_LOCALE_NOTE_MANUAL}</Text></li>
              <li>
                <Text size="xs" component="span">
                  {Localization.strings.EXTRA_LOCALE_NOTE_RESOURCES}
                  <Anchor href={resourcesUrl} target="_blank" rel="noopener noreferrer" size="xs">
                    {resourcesUrl}
                  </Anchor>
                  {Localization.strings.EXTRA_LOCALE_NOTE_RESOURCES_SUFFIX}
                </Text>
              </li>
            </ol>
          </Box>
          <Box>
            <Text size="xs" fw={600}>{Localization.strings.EXTRA_LOCALE_CONTACT_TITLE}</Text>
            <Stack gap={2} mt={4}>
              {CONTACT_KEYS.map((key) => {
                const line = Localization.strings[key];
                return line ? <Text key={key} size="xs">{line}</Text> : null;
              })}
            </Stack>
          </Box>
        </Box>
      </Paper>
    );
  }

  renderBody() {
    if (this.state.loading) {
      return (
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      );
    }
    if (this.state.loadFailed || !this.state.overview) {
      return (
        <Alert color="red" icon={<IconInfoCircle size={18} />}>
          {Localization.strings.EXTRA_LOCALE_LOAD_FAILED}
        </Alert>
      );
    }

    const overview = this.state.overview;
    const quotaSummary = (Localization.strings.EXTRA_LOCALE_QUOTA_SUMMARY || '')
      .replace('{0}', String(overview.used != null ? overview.used : 0))
      .replace('{1}', String(overview.quota != null ? overview.quota : 0));
    // The survey's opening line, with the tier (優越版/基本版…, read off the
    // granting subscription's name server-side) and the allowance filled in.
    const planLabel = overview.plan_label ? String(overview.plan_label) : '';
    const headerIntro = planLabel
      ? (Localization.strings.EXTRA_LOCALE_HEADER_INTRO || '')
        .replace('{0}', planLabel)
        .replace('{1}', String(overview.quota != null ? overview.quota : 0))
      : (Localization.strings.EXTRA_LOCALE_HEADER_INTRO_NO_PLAN || '')
        .replace('{0}', String(overview.quota != null ? overview.quota : 0));
    const resourcesUrl = Localization.strings.EXTRA_LOCALE_RESOURCES_URL
      || 'https://clst.fed.cuhk.edu.hk/evqef/resources/';

    // No minHeight: 0 here — the Stack must not shrink below its content, so
    // on a short viewport (browser zoom > 100%) the body scrolls instead of
    // clipping whatever sits at the bottom.
    return (
      <Stack gap="md" style={{ flex: 1 }}>
        {!overview.eligible && (
          <Alert color="blue" icon={<IconInfoCircle size={18} />}>
            {Localization.strings.EXTRA_LOCALE_NOT_ELIGIBLE}
          </Alert>
        )}

        {overview.eligible && (
          <Group justify="space-between" align="center">
            <Box>
              <Text fw={600} fz="lg">{Localization.strings.EXTRA_LOCALE_SELECTION_TITLE || '選擇預製教材範本'}</Text>
              <Text size="sm" c="dimmed">{quotaSummary}</Text>
            </Box>
            <Badge size="xl" variant="filled" color="brand" radius="sm" px="md">
              {(Localization.strings.EXTRA_LOCALE_REMAINING || '').replace('{0}', String(this.remaining()))}
            </Badge>
          </Group>
        )}

        {this.state.submitError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {this.state.submitError}
          </Alert>
        )}

        {overview.eligible && (
          // Grows into the spare height; never below 280px. The 280px basis
          // and the explicit minHeight are both load-bearing: with flex: 1's
          // 0% basis, or minHeight left at auto, the Stack's minimum height
          // counts every catalog row and the lists stop scrolling on their own.
          <Flex gap="md" align="stretch" style={{ flex: '1 0 280px', minHeight: 280 }}>
            <Paper
              withBorder
              radius="md"
              bg="white"
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
            >
              <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                <Text fw={600}>{Localization.strings.EXTRA_LOCALE_AVAILABLE_LIST}</Text>
              </Box>
              <ScrollArea style={{ flex: 1, minHeight: 0 }}>
                <Box p="md">{this.renderAvailableList()}</Box>
              </ScrollArea>
            </Paper>

            <Stack justify="center" align="center" gap={6} style={{ width: 92, flexShrink: 0 }}>
              <Button
                onClick={this.handleAddSelected}
                disabled={this.state.availableSelected.length === 0 || this.state.submitting}
                rightSection={<IconArrowRight size={17} />}
                px="md"
              >
                {Localization.strings.EXTRA_LOCALE_ADD || '加入'}
              </Button>
              {this.state.availableSelected.length > 0 && (
                <Text size="xs" c="dimmed">
                  {(Localization.strings.EXTRA_LOCALE_SELECTED_TO_ADD || '已選 {0} 個')
                    .replace('{0}', String(this.state.availableSelected.length))}
                </Text>
              )}
            </Stack>

            <Paper
              withBorder
              radius="md"
              bg="white"
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
            >
              <Box px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                <Group justify="space-between" gap="xs">
                  <Text fw={600}>{Localization.strings.EXTRA_LOCALE_SELECTED_LIST}</Text>
                  <Badge size="sm" variant="light">
                    {(overview.selections || []).length + this.state.picked.length}
                  </Badge>
                </Group>
              </Box>
              <ScrollArea style={{ flex: 1, minHeight: 0 }}>
                <Box p="md">{this.renderAddedList()}</Box>
              </ScrollArea>
            </Paper>
          </Flex>
        )}

        {overview.eligible && this.renderInformationPanel(headerIntro, resourcesUrl)}

        {!overview.eligible && (
          <Paper withBorder p="md" radius="md">
            <Text fw={600} mb={6}>{Localization.strings.EXTRA_LOCALE_SELECTED_LIST}</Text>
            {this.renderAddedList()}
          </Paper>
        )}
      </Stack>
    );
  }

  /**
   * Close / Submit, pinned under the scrolling body. The dialog is a fixed
   * 88vh tall; when these buttons were the body's last row, any viewport
   * shorter than the content (browser zoom > 100%, a laptop screen) clipped
   * them away with nothing to scroll.
   */
  renderFooter() {
    const overview = this.state.overview;
    if (this.state.loading || this.state.loadFailed || !overview) {
      return null;
    }
    return (
      <Group
        justify="flex-end"
        px="lg"
        py="md"
        bg="gray.0"
        style={{ flexShrink: 0, borderTop: '1px solid var(--mantine-color-gray-3)' }}
      >
        <Button variant="default" onClick={this.props.onClose}>
          {Localization.strings.CLOSE}
        </Button>
        {overview.eligible && (
          <Button
            onClick={this.handleSubmitClicked}
            disabled={this.state.picked.length === 0 || this.state.submitting}
          >
            {Localization.strings.EXTRA_LOCALE_SUBMIT}
            {this.state.picked.length > 0 ? ` (${this.state.picked.length})` : ''}
          </Button>
        )}
      </Group>
    );
  }

  render() {
    // The survey title, tier-suffixed once the overview arrives:
    // EduVenture-AI®️預製教材範本選單 (優越版)
    const overview = this.state.overview;
    const planLabel = overview && overview.plan_label ? String(overview.plan_label) : '';
    const title = (Localization.strings.EXTRA_LOCALE_TITLE || '')
      + (planLabel ? ` (${planLabel})` : '');
    return (
      <>
        <Modal
          opened={this.props.open}
          onClose={this.props.onClose}
          withCloseButton={false}
          padding={0}
          size="85%"
          radius="md"
          centered
          zIndex={2000}
          styles={{
            content: { height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
            body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0 },
          }}
        >
          {/* minHeight + nowrap: on a narrow (zoomed-in) viewport the long
              title wraps inside the bar instead of spilling out of a fixed
              56px, and the white X never wraps onto the grey body below. */}
          <Group
            justify="space-between"
            wrap="nowrap"
            px="lg"
            py={8}
            style={{ minHeight: 56, flexShrink: 0, backgroundColor: BRAND_PRIMARY }}
          >
            <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
              <IconBooks size={20} color="white" style={{ flexShrink: 0 }} />
              <Text c="white" fw={600} fz="lg">{title}</Text>
            </Group>
            <CloseButton
              onClick={this.props.onClose}
              variant="transparent"
              c="white"
              size="lg"
              style={{ flexShrink: 0 }}
            />
          </Group>
          <Box
            p="lg"
            bg="gray.0"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
          >
            {this.renderBody()}
          </Box>
          {this.renderFooter()}
        </Modal>
        {this.renderConfirmStep()}
      </>
    );
  }
}

export default ExtraLocaleDialog;
